import React, { useContext, useEffect } from "react";
import { GoogleLogin } from "react-google-login";
import FacebookLogin from "react-facebook-login";
import liff from "@line/liff";

import PropTypes from "prop-types";
import { Button, ButtonLink, TextBox, Overlay, CheckBox } from "../..";

// * ENV
const googleClientId = import.meta.env.GOOGLE_CLIENT_ID;
const lineClientId = import.meta.env.LINE_CLIENT_ID;
const facebookAppId = import.meta.env.FACEBOOK_APP_ID;

// * Styles
import "../../../styles/login.scss";

// * Images
import { ReactComponent as CloseSvg } from "../../../assets/auth/login-close.svg";
import { ReactComponent as GoogleSvg } from "../../../assets/icons/google.svg";
import { ReactComponent as FacebookSvg } from "../../../assets/icons/facebook.svg";
import { ReactComponent as LineSvg } from "../../../assets/icons/line.svg";
import { ReactComponent as WeChatSvg } from "../../../assets/icons/wechat.svg";
import { ReactComponent as ArrowRightSvg } from "../../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowRightDisabledSvg } from "../../../assets/icons/arrow-right-disabled.svg";
import { ReactComponent as EmailSvg } from "../../../assets/icons/email.svg";
import { ReactComponent as LockSvg } from "../../../assets/icons/lock.svg";
import { Form, Formik } from "formik";
import useAuth from "../../../hooks/useAuth";
import useUsers from "../../../hooks/useUsers";
import AppContext from "../../../AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { langComponent } from "../../../constants";
import { CartContext } from "../../../contexts/CartContext";

const Login = (props) => {
  liff.init({ liffId: lineClientId });

  // * Context
  const { setToken, getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.auth);

  // * Props
  const { onClose, onSwitch, onForgotPassword, onNavigateToOnboard, redirect } =
    props;
  const { signIn, signInExternal } = useAuth();
  const { queryUsers } = useUsers();
  const navigate = useNavigate();
  const { setIsFetchDetail } = useContext(CartContext);

  // * Mount
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDownEvent, false);

    return () => {
      document.removeEventListener("keydown", handleKeyDownEvent, false);
    };
  }, []);

  const handleKeyDownEvent = (e) => {
    if ((e.key ?? "").toString().toLowerCase() !== "escape") return;
    handleClose();
  };

  // * Handle closing window
  const handleClose = () => onClose();

  // * Handle switching signup/login
  const handleSwitch = () => onSwitch();

  // * Handle forgot password
  const handleForgotPassword = () => onForgotPassword();

  // * ====== Social Login Handling ======
  const handleGoogleSignInSuccess = async (e) => {
    try {
      const { googleId, profileObj } = e;
      const findUser = await queryUsers({
        googleId: googleId,
      });

      if (findUser.data.length === 0) {
        localStorage.setItem(
          "onboard:credentials",
          JSON.stringify({
            googleId: googleId,
            googleAvatar: profileObj.imageUrl,
          })
        );
        localStorage.setItem("onboard:registration-id", "google");

        return onNavigateToOnboard("/onboard");
      }

      const token = await signInExternal(googleId, "google");
      setToken(token);
      handleClose();
    } catch (error) {
      toast.error("Unable to sign in by google");
    }
  };

  const handleGoogleSignInFailure = (e) => {
    console.error(e);
  };

  const isFacebookApp = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return (
      ua.indexOf("FBAN") > -1 ||
      ua.indexOf("FBAV") > -1 ||
      ua.indexOf("Instagram") > -1
    );
  };

  const handleFacebookResponse = async (e) => {
    try {
      const { userID, picture } = e;
      // TODO image from facebook can't use error 404
      // * picture.data.url
      const findUser = await queryUsers({
        facebookId: userID,
      });

      if (findUser.data.length === 0) {
        localStorage.setItem(
          "onboard:credentials",
          JSON.stringify({
            facebookId: userID,
            facebookAvatar: picture?.data?.url ?? undefined,
          })
        );
        localStorage.setItem("onboard:registration-id", "facebook");
        return onNavigateToOnboard("/onboard");
      }

      const token = await signInExternal(userID, "facebook");
      setToken(token);
      handleClose();
    } catch (error) {
      console.log("error", error);
      toast.error("Unable to sign in by facebook");
    }
  };

  const handleLineRequest = () => {
    liff.login();
  };

  // * Handle submit
  const handleSubmit = async (values) => {
    const { username, password, stayLoggedIn } = values;
    try {
      if (stayLoggedIn) {
        localStorage.setItem("rememberMe", "true");
      }

      const token = await signIn(username, password);
      setToken(token);
      toast.success(`Login Success!`);
      setIsFetchDetail(true);
      handleClose();
    } catch (error) {
      toast.error(`Username or Password Incorrect.`);
    }
  };

  return (
    <Overlay
      onClose={onClose}
      content={
        <div className="login">
          <h1 className="login-header">{Translation("Login")}</h1>
          <Formik
            initialValues={{ username: "", password: "", stayLoggedIn: false }}
            validate={({ username, password, stayLoggedIn }) => {
              let errors = {};

              if (!username) {
                errors.username = Translation("Username cannot be blank");
              }

              if (!password) {
                errors.password = Translation("Password cannot be blank");
              }

              return errors;
            }}
            onSubmit={handleSubmit}
          >
            {({
              values: { username, password, stayLoggedIn },
              setFieldValue,
            }) => {
              const submittable = username && password;
              return (
                <>
                  <Form className="login-form">
                    <TextBox
                      name="username"
                      placeholder={Translation("Email")}
                      type="text"
                      leadingIcon={<EmailSvg />}
                      values={username}
                    />
                    <TextBox
                      name="password"
                      placeholder={Translation("Password")}
                      type="password"
                      leadingIcon={<LockSvg />}
                      values={password}
                    />
                    <div className="login-remember">
                      <CheckBox
                        name="stayLoggedIn"
                        defaultValue={stayLoggedIn}
                        className="login-remember__checkbox"
                        label={Translation("Remember me")}
                        onChange={(checked) => {
                          setFieldValue("stayLoggedIn", checked);
                        }}
                      />
                      <ButtonLink
                        content={Translation("Forgot Password")}
                        onClick={handleForgotPassword}
                      />
                    </div>
                    <Button
                      type="submit"
                      label={Translation("LOGIN")}
                      trailingIcon={
                        submittable ? (
                          <ArrowRightSvg />
                        ) : (
                          <ArrowRightDisabledSvg />
                        )
                      }
                      style={{ margin: "40px 0", width: "300px" }}
                      disabled={!submittable}
                    />
                  </Form>
                </>
              );
            }}
          </Formik>
          <span>{Translation("Or login with")}</span>
          <div className="login__socials">
            <div className="login__socials-link login__socials-link__google">
              <GoogleLogin
                clientId={googleClientId}
                onSuccess={handleGoogleSignInSuccess}
                onFailure={handleGoogleSignInFailure}
                cookiePolicy={"single_host_origin"}
                render={(renderProps) => (
                  <GoogleSvg onClick={renderProps.onClick} />
                )}
              />
            </div>
            <FacebookLogin
              appId={facebookAppId}
              autoLoad={false}
              fields="name,email,picture.width(500).height(500)"
              callback={handleFacebookResponse}
              cssClass="login__socials-link login__socials-link__facebook"
              size="small"
              textButton=""
              icon={<FacebookSvg />}
              disableMobileRedirect={!isFacebookApp()}
              redirectUri={"https://urbaancommunities.com/facebook"}
            />
            <div className="login__socials-link login__socials-link__line">
              <LineSvg onClick={handleLineRequest} />
            </div>
          </div>
          <div className="login__switch">
            <span>{Translation("Don't have an account yet?")}</span>
            <ButtonLink
              content={Translation("Sign Up")}
              onClick={handleSwitch}
            />
          </div>
        </div>
      }
    />
  );
};

CheckBox.propTypes = {
  onClose: PropTypes.func,
  onSwitch: PropTypes.func,
  onForgotPassword: PropTypes.func,
};

CheckBox.defaultProps = {
  onClose: () => {},
  onSwitch: () => {},
  onForgotPassword: () => {},
};

export default Login;
